/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import {
  Button,
  buttonVariants,
  Textarea,
  FieldLabel,
  FieldError,
  Field,
} from '@e2n/ui';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { DatePicker } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/Datepicker';
import { StandortSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/StandortSelect';
import { getDefaultVon } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/utils/getDefaultVon';
import { useCreateArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateArbeitsbereichStation/useCreateArbeitsbereichStation';
import { useRotationsplanById } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanById/useRotationsplanById';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toast } from '@/hooks/use-toast';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import z from 'zod';
import { hasUeberschneidungen } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/utils/hasUeberschneidungen';
import { ArbeitsbereichSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/ArbeitsbereichSelect';
import { Suspense } from 'react';
import { CREATE_FORM_ID } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/constants';
import { useCreateAndUpdateSchema } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/formSchema/useCreateAndUpdateSchema';
import { SheetHeader, SheetTitle, Skeleton } from '@e2n/ui';
import { cn } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';

export function CreateStation() {
  const { t } = useTranslation();
  const { id, abschnittId } = useParams();
  const [createArbeitsbereichStation] = useCreateArbeitsbereichStation();
  const { standorte, currentStandort } = useStandortContext();
  const standortIds = standorte.map((s) => s.id);
  const { rotationsplan } = useRotationsplanById(standortIds, id);
  const navigate = useNavigate();

  if (!rotationsplan || !abschnittId) {
    navigate(`/mitarbeiter/rotationsplaene`);
  }

  const schema = useCreateAndUpdateSchema();

  const form = useForm({
    defaultValues: (() => {
      const defaultVon = getDefaultVon(rotationsplan);
      const defaultBis = dayjs(defaultVon).add(7, 'day').toDate();

      return {
        standort: new Standort({
          id: currentStandort.id,
          name: currentStandort.name,
          storenummer: currentStandort.storeNummer,
        }),
        von: defaultVon,
        bis: defaultBis,
        lernziele: '',
        arbeitsbereich: undefined,
      } satisfies z.infer<typeof schema> as z.infer<typeof schema>;
    })(),
    validators: {
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await createArbeitsbereichStation({
          input: {
            abschnittId,
            arbeitsbereichId: value.arbeitsbereich.id,
            lernziele: value.lernziele,
            von: dayjs(value.von).format('YYYY-MM-DD'),
            bis: dayjs(value.bis).format('YYYY-MM-DD'),
            rotationsplanId: rotationsplan.id,
          },
        });

        if (result.data.mitarbeiter_createArbeitsbereichStation.station) {
          navigate(`/mitarbeiter/rotationsplan/${rotationsplan.id}`);
          toast({
            title: t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.success',
            ),
          });
        } else {
          toast({
            title: t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.error',
            ),
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.error',
          ),
        });
      }
    },
  });

  return (
    <>
      <SheetHeader className="space-y-0 flex flex-row items-center justify-between bg-neutrals-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <Link
            to={`/mitarbeiter/rotationsplan/${id}`}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }))}>
            <FontAwesomeIcon
              className="text-primary-blue-600"
              icon={faArrowLeft}
            />
          </Link>
          <SheetTitle className="text-sm text-neutrals-800">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.title',
            )}
          </SheetTitle>
        </div>
        <Button
          className="flex md:hidden"
          form={CREATE_FORM_ID}
          variant="ghost-primary">
          {t('actions.fertig')}
        </Button>
      </SheetHeader>
      <div className="flex flex-col gap-4 px-6 grow">
        <div className="text-xs text-neutrals-700">
          {t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
          )}
        </div>
        <form
          id={CREATE_FORM_ID}
          className="flex flex-col gap-4 h-full"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}>
          <form.Field
            name="standort"
            listeners={{
              onChange: () => form.resetField('arbeitsbereich'),
            }}>
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full">
                  <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                    {t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.standort.label',
                    ) + '*'}
                  </FieldLabel>
                  <StandortSelect
                    handleChange={field.handleChange}
                    value={field.state.value}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="arbeitsbereich">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full">
                  <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                    {t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.arbeitsbereich.label',
                    ) + '*'}
                  </FieldLabel>
                  <Suspense fallback={<Skeleton className="w-108 h-10" />}>
                    <ArbeitsbereichSelect
                      value={field.state.value}
                      standortId={
                        form.getFieldValue('standort.id') ?? currentStandort.id
                      }
                      handleChange={field.handleChange}
                      mitarbeiter={rotationsplan.mitarbeiter}
                    />
                  </Suspense>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <div className="flex flex-col md:flex-row gap-4">
            <form.Field
              name="von"
              validators={{
                onChangeListenTo: ['bis'],
                onChange: ({ value }) => {
                  return value !== undefined &&
                    form.getFieldValue('bis') &&
                    dayjs(value).isAfter(form.getFieldValue('bis'))
                    ? {
                        message: t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.endeVorBeginn',
                        ),
                      }
                    : undefined;
                },
              }}
              listeners={{
                onChange: ({ value, fieldApi }) => {
                  const alleStationen = rotationsplan.getAllStationen();
                  const showWarning = hasUeberschneidungen(
                    alleStationen,
                    undefined,
                    value,
                    fieldApi.form.getFieldValue('bis'),
                  );
                  if (showWarning) {
                    fieldApi.setMeta((prev) => ({
                      ...prev,
                      warnings: [
                        {
                          message: t(
                            'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.hasUeberschneidung',
                          ),
                        },
                      ],
                    }));
                  } else {
                    fieldApi.setMeta((prev) => ({
                      ...prev,
                      warnings: undefined,
                    }));
                  }
                },
              }}>
              {(field) => {
                const isInvalid = !field.state.meta.isValid;

                const hasWarning =
                  // @ts-expect-error Es gibt aktuell leider noch keine Möglichkeit Typescript mitzuteilen, dass
                  // das Tanstack MetaField um eine Custom Property erweitert wurde, daher übergangsweise der harte Weg
                  // s. https://github.com/TanStack/form/issues/1111
                  field.state.meta.warnings;

                return (
                  <Field
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1 w-full">
                    <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                      {t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.von.label',
                      ) + '*'}
                    </FieldLabel>
                    <DatePicker
                      defaultDate={field.state.value}
                      onSelect={field.handleChange}
                      drawerLabel={t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.von.label',
                      )}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {hasWarning && (
                      <FieldError
                        variant="warning"
                        // @ts-expect-error Es gibt aktuell leider noch keine Möglichkeit Typescript mitzuteilen, dass
                        // das Tanstack MetaField um eine Custom Property erweitert wurde, daher übergangsweise der harte Weg
                        errors={field.state.meta.warnings}
                      />
                    )}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field
              name="bis"
              validators={{
                onChangeListenTo: ['von'],
                onChange: ({ value }) => {
                  return value !== undefined &&
                    form.getFieldValue('von') &&
                    dayjs(form.getFieldValue('von')).isAfter(value)
                    ? {
                        message: t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.beginnVorEnde',
                        ),
                      }
                    : undefined;
                },
              }}
              listeners={{
                onChange: ({ value, fieldApi }) => {
                  const alleStationen = rotationsplan.getAllStationen();
                  const showWarning = hasUeberschneidungen(
                    alleStationen,
                    undefined,
                    fieldApi.form.getFieldValue('von'),
                    value,
                  );
                  if (showWarning) {
                    fieldApi.setMeta((prev) => ({
                      ...prev,
                      warnings: [
                        {
                          message: t(
                            'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.hasUeberschneidung',
                          ),
                        },
                      ],
                    }));
                  } else {
                    fieldApi.setMeta((prev) => ({
                      ...prev,
                      warnings: undefined,
                    }));
                  }
                },
              }}>
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                const hasWarning =
                  // @ts-expect-error Es gibt aktuell leider noch keine Möglichkeit Typescript mitzuteilen, dass
                  // das Tanstack MetaField um eine Custom Property erweitert wurde, daher übergangsweise der harte Weg
                  // s. https://github.com/TanStack/form/issues/1111
                  field.state.meta.isTouched && field.state.meta.warnings;

                return (
                  <Field
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1 w-full">
                    <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                      {t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.bis.label',
                      ) + '*'}
                    </FieldLabel>
                    <DatePicker
                      defaultDate={field.state.value}
                      onSelect={field.handleChange}
                      drawerLabel={t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.bis.label',
                      )}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {hasWarning && (
                      <FieldError
                        variant="warning"
                        // @ts-expect-error Es gibt aktuell leider noch keine Möglichkeit Typescript mitzuteilen, dass
                        // das Tanstack MetaField um eine Custom Property erweitert wurde, daher übergangsweise der harte Weg
                        errors={field.state.meta.warnings}
                      />
                    )}
                  </Field>
                );
              }}
            </form.Field>
          </div>
          <form.Field name="lernziele">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field
                  data-invalid={isInvalid}
                  className="flex flex-col gap-1 w-full">
                  <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                    {t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.lernziele.label',
                    )}
                  </FieldLabel>
                  <Textarea
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
        </form>
      </div>
      <div className="h-px w-full bg-neutrals-300" />
      <div className="hidden md:flex justify-end px-8 pb-4">
        <Button
          type="submit"
          disabled={!form.state.isValid || form.state.isSubmitting}
          form={CREATE_FORM_ID}>
          {t('actions.fertig')}
        </Button>
      </div>
    </>
  );
}
